import { ProfileReportReasonType } from '@diritto/common/account/constants/profile.constant';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import styled from '@emotion/styled';
import { ButtonType } from 'constants/button';
import { isPC } from 'constants/layout';
import { ModalParams, ModalType } from 'constants/modal';
import { useCallback, useEffect, useState } from 'react';
import { Button } from 'ui-components/common/Button';
import { RadioButton } from 'ui-components/common/RadioButton';
import { TextArea } from 'ui-components/common/TextArea';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { useMedia } from 'ui-hooks/useMediaQuery';
import { useShowModal } from 'ui-hooks/useShowModal';
import { ModalHandler } from 'utils/ModalHandler';

export const ProfileReportModal = () => {
  const { isPc } = useMedia();
  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const [state, setState] = useState<ModalParams[ModalType.ProfileReport]>();
  const [reason, setReason] = useState<ProfileReportReasonType>();
  const [reasonDescription, setReasonDescription] = useState<string>();

  const handleHide = useCallback(() => {
    hideModal();
    setReason(undefined);
    setReasonDescription(undefined);
  }, [hideModal]);

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    handleHide();
  }, [handleHide, state]);

  const handleSubmit = useCallback(() => {
    if (reason) {
      state?.onSubmit?.({ reason, reasonDescription });
      handleHide();
    }
  }, [handleHide, reason, reasonDescription, state]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.ProfileReport]) => {
      setState(params);

      if (show) {
        showModal();
      } else {
        handleHide();
      }
    };

    ModalHandler.addEventListener(ModalType.ProfileReport, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.ProfileReport);
    };
  }, [handleHide, showModal]);
  return (
    <ModalLayout isShow={isShow} revealModal={revealModal} isBottom={!isPc}>
      <StyledModal>
        <StyledDescriptionTitle>프로필 신고</StyledDescriptionTitle>
        <StyledRadioGuideMessage>
          적합하지 않은 신고나 허위 신고가 누적될 경우 제재를 받을 수 있어요.
        </StyledRadioGuideMessage>
        <StyledRadioWrapper>
          <StyledRadioButton
            id="Slander"
            showName
            name="욕설 / 비방 / 차별 / 혐오"
            value={ProfileReportReasonType.Slander}
            selectedValue={reason}
            onChange={() => setReason(ProfileReportReasonType.Slander)}
          />
          <StyledRadioButton
            id="Spam"
            showName
            name="영리목적 / 홍보성 / 스팸"
            value={ProfileReportReasonType.Spam}
            selectedValue={reason}
            onChange={() => setReason(ProfileReportReasonType.Spam)}
          />
          <StyledRadioButton
            id="ExposurePersonalInformation"
            showName
            name="개인정보 노출"
            value={ProfileReportReasonType.ExposurePersonalInformation}
            selectedValue={reason}
            onChange={() => setReason(ProfileReportReasonType.ExposurePersonalInformation)}
          />
          <StyledRadioButton
            id="CopyrightInfringement"
            showName
            name="명의 도용 / 저작권 침해"
            value={ProfileReportReasonType.CopyrightInfringement}
            selectedValue={reason}
            onChange={() => setReason(ProfileReportReasonType.CopyrightInfringement)}
          />
          <StyledRadioButton
            id="AbuseForRewards"
            showName
            name="리워드를 위한 부정 이용"
            value={ProfileReportReasonType.AbuseForRewards}
            selectedValue={reason}
            onChange={() => setReason(ProfileReportReasonType.AbuseForRewards)}
          />
          <StyledRadioButton
            id="Other"
            showName
            name="기타 사유"
            value={ProfileReportReasonType.Other}
            selectedValue={reason}
            onChange={() => setReason(ProfileReportReasonType.Other)}
          />
        </StyledRadioWrapper>
        {reason === ProfileReportReasonType.Other && (
          <StyledCommentTextArea
            placeholder="기타 사유를 작성해 주세요. (500자 제한)"
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReasonDescription(e.target.value)}
          />
        )}
        <StyledButtons>
          <StyledButton label="신고하기" onClick={handleSubmit} disabled={!reason} />
          <StyledButton label="닫기" buttonType={ButtonType.SecondaryNoBorder} onClick={handleClose} />
        </StyledButtons>
      </StyledModal>
    </ModalLayout>
  );
};

const StyledModal = styled.div`
  width: 100vw;
  border-radius: 16px 16px 0 0;
  padding: 32px 24px 0 24px;

  @media (${isPC}) {
    width: 400px;
    border-radius: 16px;
    padding: 32px 24px 16px 24px;
  }
  background-color: ${Color.White};

  display: flex;
  flex-direction: column;
  align-items: center;
  ${SafeAreaBottomStyles(16)}
`;

const StyledDescriptionTitle = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
  color: ${Color.Black};
  text-align: left;
  width: 100%;
`;

const StyledRadioGuideMessage = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin: 8px 0;
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
  color: ${Color.Gray500};
`;

const StyledRadioWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledRadioButton = styled(RadioButton)`
  display: flex;
  align-items: center;
  height: 40px;
`;

const StyledButtons = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledCommentTextArea = styled(TextArea)`
  width: 100%;
  height: 48px;
  border: 1px solid ${Color.Gray400};
  border-radius: 4px;
  padding: 13px 16px;
  margin-top: 4px;
  ${Typography.XXSmall};

  &:focus {
    outline: none;
    border: 1px solid ${Color.Primary500};
  }
`;
